import Campaign from '../store/Campaign';
import { CampaignEndSignal } from '../store/schema/CampaignEndSignal';
import { CampaignQueue } from '../store/CampaignQueue';
import { CampaignType } from '../store/schema/CampaignType';
import { END_CAMPAIGN, RENDER_CAMPAIGN } from 'mini-host-bridge/lib/nativeMessageConstants';
import { endCampaign } from '../utils/endCampaign';
import { isMiniHostAppFeatureEnabled } from 'mini-hostapp-feature-flags/lib/isMiniHostAppFeatureEnabled';
import { registerScenarioDataListener } from 'mini-host-bridge/lib/utils/scenarioUpdateHandlers';
import { sendScenarioDataToHost } from 'mini-host-bridge/lib/utils/sendScenarioDataToHost';

type CampaignOpxHostType = {};

/**
 * Campaign type for campaigns to be rendered by the host app layer.
 * In-order to do so, the host layer needs to:
 *   - Listen to a message "RENDER_CAMPAIGN" to render the campaign
 *   - Send a message "END_CAMPAIGN" back to the web-layer on end of the campaign
 *
 * Example usage: In-app review campaign by Outlook Lite.
 */
export class CampaignOpxHost implements CampaignType<CampaignOpxHostType> {
    private static hostCampaignQueue = CampaignQueue.getInstance();

    public start(campaign: Campaign<CampaignOpxHostType>): void {
        if (!isMiniHostAppFeatureEnabled('hostCampaigns')) {
            return;
        }

        CampaignOpxHost.hostCampaignQueue.addToQueue(campaign);

        // Listen to OPX message from native-layer to end the campaign.
        registerScenarioDataListener(scenarioData => {
            const { type, message } = scenarioData;
            if (type === END_CAMPAIGN && message.campaignId === campaign.getRemoteCampaignId()) {
                this.end(campaign, CampaignEndSignal.OnUserDismiss);
            }
        });
    }

    public ready(campaign: Campaign<CampaignOpxHostType>): void {
        // Send OPX message to native-layer to render its UI.
        sendScenarioDataToHost({
            type: RENDER_CAMPAIGN,
            data: { campaignId: campaign.getRemoteCampaignId() },
        });
    }

    public end(campaign: Campaign<CampaignOpxHostType>, endSignal: CampaignEndSignal): void {
        endCampaign(campaign, endSignal);
    }

    public render(): JSX.Element {
        // No UI needed here as host knows what to render
        return null;
    }
}
